import { useMemo, useCallback, useState, useEffect } from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { LibraryBrowser } from "@/ui-components/LibraryBrowser";
import { portfolioToLibrarySections } from "@/ui-components/LibraryBrowser/adapters";
import {
  useWatchlistCreator,
  type WatchlistCreatorMode,
  type SavedWatchlist,
  type EditableWatchlist,
} from "../hooks/useWatchlistCreator";
import WatchlistBuilderPane from "./WatchlistBuilderPane";
import BuilderCanvas from "./BuilderCanvas";
import type { WatchlistDraft } from "./DescribeItPanel";
import type { FilterDraft } from "../hooks/useFilterBuilder";
import { menuCategories } from "@/widget-components/newsquawk-headlines/models/categories";
import type { Portfolio } from "@/types/Portfolio";
import type { TagsWatchlist } from "@/shared/api/pref-storage/tags-watchlists";
import { useTagsWatchlistsCRUD } from "@/shared/api/pref-storage/tags-watchlists/useTagsWatchlistsCRUD";
import { useHeadlineQueriesCRUD } from "@/widget-components/newsquawk-headlines/api/headline-query/useHeadlineQueriesCRUD";
import useAuth from "@/hooks/useAuth";
import clsx from "clsx";

// Build tag ID -> name lookup
const tagIdToName: Record<string, string> = {};
menuCategories.forEach((cat) => {
  cat.items.forEach((item) => {
    tagIdToName[String(item.id)] = item.title;
  });
});

interface WatchlistCreatorBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  mode: WatchlistCreatorMode;
  /** Pass an existing portfolio to enable edit mode (ticker/keyword) */
  editingPortfolio?: Portfolio | null;
  /** Pass an existing tag watchlist to enable edit mode (tag) */
  editingTagWatchlist?: TagsWatchlist | null;
  /** Concierge draft pre-fill (create mode) */
  draft?: { name?: string; tickers?: string[]; keywords?: string[]; tags?: string[]; basedOn?: string } | null;
  /**
   * Render in place rather than as a modal. The Watchlists page IS the
   * builder, so it embeds this directly; widgets still open it as a dialog.
   */
  inline?: boolean;
  /** Replaces the modal title bar when inline (e.g. a Browse library button). */
  headerSlot?: React.ReactNode;
  /** Inline only — the concierge's hand-off to the AND/OR filter builder. */
  onOpenInBuilder?: (draft: FilterDraft) => void;
  /** Inline only — the concierge's hand-off of a single proposed watchlist. */
  onOpenWatchlist?: (draft: WatchlistDraft) => void;
  /** Fired after a save, with what was saved. When supplied it replaces the
   *  old behaviour of simply closing — the point of saving is to see the
   *  thing you made, not to be sent back to a file browser. Inline, it also
   *  covers combinations and whatever the concierge creates. */
  onSaved?: (saved?: SavedWatchlist) => void;
  /** Inline only — opening one of the user's own lists to edit. */
  onEditWatchlist?: (item: EditableWatchlist) => void;
  /** Inline only — a library list, opened as an editable copy. */
  onBuildOn?: (item: EditableWatchlist) => void;
  /** Fired when the form is cancelled, so the host can drop what was being
   *  edited — otherwise reset() refills the form from it and the builder
   *  never closes. */
  onCancelled?: () => void;
}

// One noun. The dialog used to announce "Edit Keyword Watchlist", telling the
// user which of our storage buckets they had landed in — a distinction they
// never chose and cannot act on.
const CREATE_TITLE = "New watchlist";
const EDIT_TITLE = "Edit watchlist";

export default function WatchlistCreatorBrowser({ isOpen, onClose, mode, editingPortfolio, editingTagWatchlist, draft, inline = false, headerSlot, onOpenInBuilder, onOpenWatchlist, onEditWatchlist, onBuildOn, onSaved, onCancelled }: WatchlistCreatorBrowserProps) {
  const { authenticated } = useAuth();

  const creator = useWatchlistCreator({
    mode,
    editingPortfolio,
    editingTagWatchlist,
    draft,
    // Inline, BuilderCanvas reports every save — it has to close the form as
    // well — so the hook reporting too would announce each one twice.
    onCreated: inline ? undefined : (onSaved ?? onClose),
    // Inline, onClose is "go to the library", which after a delete left the
    // deleted list held as the one being edited. Dropping it returns to the
    // list of your watchlists, which is where you can see it has gone.
    onDeleted: inline ? onCancelled : onClose,
  });

  // Everything you could build from: your own lists as well as Newsquawk's.
  //
  // This used to pull out only the Newsquawk group matching the creator's
  // mode, so you could neither reuse your own watchlists nor pick one of a
  // different type. Both are now just sources.
  /**
   * Every list the user has, whatever store it lives in.
   *
   * Tag watchlists and combined watchlists used to be passed as empty arrays,
   * so "Your watchlists" only ever listed portfolios — anything created by the
   * concierge or the combine route was simply absent from the library the
   * builder browses, which read as "my new watchlist hasn't saved".
   */
  const { watchlistsQuery } = useTagsWatchlistsCRUD();
  const { queriesQuery } = useHeadlineQueriesCRUD();

  const sections = useMemo(
    () =>
      portfolioToLibrarySections(
        creator.portfolios,
        watchlistsQuery.data ?? [],
        queriesQuery.data ?? [],
        { tagIdToName }
      ),
    [creator.portfolios, watchlistsQuery.data, queriesQuery.data]
  );

  /**
   * What can actually be pulled into the list being built.
   *
   * A portfolio's terms can be imported; a tag watchlist's tags and a combined
   * watchlist's operators cannot — they are different records, not different
   * flavours of the same one. Those were previously silently inert: clicking
   * did nothing, with no indication why, so the library looked half-broken.
   *
   * They are now marked disabled, which greys them uniformly. Being able to
   * see the whole library and being told which parts this form can take is
   * clearer than a list where only some rows respond and nothing says which.
   */
  const importableSections = useMemo(
    () =>
      sections.map((section) => ({
        ...section,
        groups: section.groups.map((group) => ({
          ...group,
          items: group.items.map((item) => {
            // Ids are namespaced by store here: "tag-" for tag watchlists,
            // "filter-" for combined ones, bare for portfolios.
            const isRecord =
              item.id.startsWith("tag-") || item.id.startsWith("filter-");

            // In tag mode the bare ids under "nq-library" are individual tags,
            // which this form does take; the bare ids under "Your watchlists"
            // are portfolios, which it does not. Same id shape, different
            // meaning per section — so the section has to be part of the test.
            const importable =
              mode === "tag"
                ? !isRecord && section.id === "nq-library"
                : !isRecord;

            return importable ? item : { ...item, disabled: true };
          }),
        })),
      })),
    [sections, mode]
  );

  /**
   * ContentArea reads `selections[sectionId]`, and this adapter produces two
   * sections — "my-watchlists" and "nq-library". Writing only the latter meant
   * nothing under "Your watchlists" could ever show as ticked: clicking it
   * changed the creator's state but the checkbox never moved, so the user's own
   * lists looked inert while Newsquawk's worked.
   *
   * The same ids go under both keys. A section only ever renders its own items
   * and the ids are namespaced by store here (`tag-`, `filter-`, bare for
   * portfolios), so an id can only ever tick in the section that holds it.
   */
  const browserSelections = useMemo<Record<string, string[]>>(() => {
    const ids =
      mode === "tag"
        ? creator.selectedTags.map((t) => String(t.id))
        : [...creator.importedWatchlistIds].map(String);
    if (ids.length === 0) return {} as Record<string, string[]>;
    return { "my-watchlists": ids, "nq-library": ids };
  }, [mode, creator.importedWatchlistIds, creator.selectedTags]);

  // Handle toggle from LibraryBrowser
  const handleToggle = useCallback((sectionId: string, itemId: string) => {
    if (mode === "tag") {
      // For tags, find the tag item and toggle it
      const tagId = Number(itemId);
      if (!isNaN(tagId)) {
        const tagItem = menuCategories
          .flatMap((c) => c.items)
          .find((item) => item.id === tagId);
        if (tagItem) {
          creator.toggleTag({ title: tagItem.title, id: tagItem.id });
        }
      }
    } else {
      // For ticker/keyword, toggle the watchlist import
      const portfolioId = Number(itemId);
      if (!isNaN(portfolioId)) {
        creator.toggleWatchlistImport(portfolioId);
      }
    }
  }, [mode, creator.toggleTag, creator.toggleWatchlistImport]);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const handleClose = useCallback(() => {
    creator.reset();
    onClose();
  }, [creator.reset, onClose]);

  const dialogHeader = (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-700/50 bg-gray-800">
      <h2 className="text-sm font-semibold text-gray-100 flex-1">
        {creator.isEditing ? EDIT_TITLE : CREATE_TITLE}
      </h2>
      <button
        onClick={handleClose}
        className="p-1 text-gray-400 hover:text-gray-200 transition-colors"
      >
        <XMarkIcon className="size-5" />
      </button>
    </div>
  );

  const browser = isMobile ? (
    <div className="h-full flex flex-col">
      {headerSlot ?? dialogHeader}
      <div className="flex-1 overflow-y-auto">
        <WatchlistBuilderPane creator={creator} onCancel={handleClose} />
      </div>
    </div>
  ) : (
    <LibraryBrowser
      mode="builder"
      sections={importableSections}
      selections={browserSelections}
      onSelectionsChange={() => {}}
      onToggleOverride={handleToggle}
      className={inline ? "h-full" : "h-full !rounded-none !border-0"}
      sidebarTitle={mode === "tag" ? "Tags" : "Newsquawk"}
      customRightPane={<WatchlistBuilderPane creator={creator} onCancel={handleClose} />}
      header={headerSlot ?? dialogHeader}
      /* No defaultActivePath: opening straight into the whole Newsquawk
         taxonomy made a wall of checkboxes the first thing in the editor,
         outweighing the form that is actually the job. They appear when
         "Newsquawk watchlists" is chosen. */
    />
  );

  if (inline) {
    return (
      /* The landing is as tall as what it holds, up to two thirds of the page,
         and the headline strip below gets the rest; the form and the
         concierge (data-building) keep a fixed two thirds so the strip doesn't
         jump about as terms are added. Signed in, the strip is there at every
         width; logged out, on wide screens only — hence the cap's breakpoint. */
      <div
        className={clsx(
          "flex min-h-0 flex-[0_1_auto] flex-col overflow-hidden rounded-lg border border-gray-700/50 bg-gray-800 has-[>[data-building]]:flex-[2_1_0%]",
          authenticated ? "max-h-[66%]" : "xl:max-h-[66%]"
        )}
      >
        {headerSlot}
        <BuilderCanvas
          creator={creator}
          onOpenInBuilder={onOpenInBuilder ?? (() => {})}
          onOpenAdvanced={onOpenInBuilder}
          onOpenWatchlist={onOpenWatchlist}
          onSaved={onSaved ?? (() => {})}
          onCancel={
            inline
              ? () => {
                  creator.reset();
                  onCancelled?.();
                }
              : handleClose
          }
          onEditWatchlist={onEditWatchlist ?? (() => {})}
          onBuildOn={onBuildOn ?? (() => {})}
        />
      </div>
    );
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} className="no-drag relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-900/90 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200"
      />
      <div className="fixed inset-0 z-10 flex items-stretch md:items-center md:justify-center md:p-4">
        <DialogPanel
          transition
          className="w-full md:mx-4 md:h-[85vh] transform overflow-hidden md:rounded-lg bg-gray-800 shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[closed]:sm:scale-95"
        >
          {browser}
        </DialogPanel>
      </div>
    </Dialog>
  );
}
